import { EventEmitter, Injectable } from '@angular/core';
import type { ColumnState } from 'ag-grid-community';

import { propertyOf } from '../../../../../core/utils/is-record.util';
import type { ITableItem, TableId } from '../../../../../project/models';
import type { FilteredTableId, IFilteringTableColumnsCreateParams } from '../../../filtered-table/models';

interface ColumnChanges {
  visibility: string;
  ordering: string;
  width: string;
  tableId: TableId | undefined;
}

interface FilteredTableColumnChanges extends ColumnChanges {
  filtering: string;
  sorting: string;
  filteredTableId: FilteredTableId | undefined;
}

interface TableStates {
  visibility: unknown;
  ordering: unknown;
  width: unknown;
  tableId: TableId | undefined;
}

interface FilteringTableStates extends TableStates {
  filterModel: unknown;
  sortingModel: unknown;
  filteredTableId: FilteredTableId | undefined;
}

@Injectable({
  providedIn: 'root',
})
export class DetectUnsavedTableChangesService {
  readonly tableColumnsSaved$: EventEmitter<void> = new EventEmitter<void>();

  filteredTableColumnChanges: FilteredTableColumnChanges = {
    filtering: '',
    sorting: '',
    visibility: '',
    ordering: '',
    width: '',
    tableId: undefined,
    filteredTableId: undefined,
  };

  defaultTableColumnChanges: ColumnChanges = {
    visibility: '',
    ordering: '',
    width: '',
    tableId: undefined,
  };

  setFilteringTableValues({ filterModel, sortingModel, visibility, ordering, width, tableId, filteredTableId }: FilteringTableStates) {
    this.filteredTableColumnChanges.filtering = JSON.stringify(filterModel);
    this.filteredTableColumnChanges.sorting = JSON.stringify(sortingModel);
    this.filteredTableColumnChanges.visibility = JSON.stringify(visibility);
    this.filteredTableColumnChanges.ordering = JSON.stringify(ordering);
    this.filteredTableColumnChanges.width = JSON.stringify(width);
    this.filteredTableColumnChanges.filteredTableId = filteredTableId;
    this.filteredTableColumnChanges.tableId = tableId;
  }

  setTableValues({ visibility, ordering, width, tableId }: TableStates) {
    this.defaultTableColumnChanges.visibility = JSON.stringify(visibility);
    this.defaultTableColumnChanges.ordering = JSON.stringify(ordering);
    this.defaultTableColumnChanges.width = JSON.stringify(width);
    this.defaultTableColumnChanges.tableId = tableId;
  }

  getFilteringTableStates(
    filterModel: unknown,
    apiColumnsState: ColumnState[],
    columnsState: IFilteringTableColumnsCreateParams[],
    tableId: TableId,
    filteredTableId: FilteredTableId,
  ) {
    return {
      filterModel,
      sortingModel: apiColumnsState
        .filter(c => c.sort)
        .map(({ colId, sort, sortIndex }) => ({
          colId,
          sort,
          sortIndex,
        })),
      visibility: columnsState.map(({ hidden: isHidden }) => isHidden),
      ordering: columnsState.map(({ order, dataattribute, datacolumn }) => ({ order, dataattribute, datacolumn })),
      width: columnsState.map(({ width }) => width),
      tableId,
      filteredTableId,
    };
  }

  getTableStates(table: ITableItem | undefined) {
    return {
      visibility: table?.columns.map(({ hidden: isHidden, id }) => ({ hidden: isHidden, id })),
      ordering: table?.columns.map(({ order, dataattribute, datacolumn }) => ({ order, dataattribute, datacolumn })),
      width: table?.columns.map(({ width, id }) => ({ width, id })),
      tableId: table?.id,
    };
  }

  checkFilteredTableChanges({
    filterModel,
    sortingModel,
    visibility,
    ordering,
    width,
    filteredTableId,
    tableId,
  }: FilteringTableStates): boolean {
    const { filteredTableId: savedFilteredTableId, tableId: savedTableId } = this.filteredTableColumnChanges;
    if (savedFilteredTableId !== filteredTableId || savedTableId !== tableId) {
      return true;
    }

    return this.isEqual(this.filteredTableColumnChanges, {
      filtering: filterModel,
      sorting: sortingModel,
      visibility,
      ordering,
      width,
    });
  }

  checkTableChanges({ visibility, ordering, width, tableId }: TableStates): boolean {
    if (this.defaultTableColumnChanges.tableId !== tableId) {
      return true;
    }

    return this.isEqual(this.defaultTableColumnChanges, { visibility, ordering, width });
  }

  private isEqual(saved: ColumnChanges | FilteredTableColumnChanges, current: Record<string, unknown>): boolean {
    return Object.keys(current).every(key => propertyOf(saved, key) === JSON.stringify(current[key]));
  }
}
